import React, { useState, useContext } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { SHA256, enc } from "crypto-js";
import jsonToCsv from "../../services/utils";
import AuthContext from '../../context/AuthContext';
import LoadingBackdrop from '../LoadingBackdrop'; 
import { localDayStartToUtcDate } from "../../services/dateUtils";

const ReportForm = ({ setData, setSnackOpen, setMessageOpen, setSeverity }) => {
  const { token, acquirerData } = useContext(AuthContext);
  const [currency, setCurrency] = useState(acquirerData?.currency_code || "");
  const [reportDate, setReportDate] = useState("");
  const [hide, setHide] = useState(true);
  const [csvData, setCsvData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const minDate = () => {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    return ninetyDaysAgo.toISOString().split("T")[0];
  };

  const maxDate = () => {
    const today = new Date();
    today.setDate(today.getDate() - 1);
    return today.toISOString().split("T")[0];
  };

  const handleDateChange = (event) => {
    const selectedDate = event.target.value;
    setReportDate(selectedDate);
  };

  const handleDownload = () => {
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `report_clearing_settlement_${reportDate}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setRetryCount(0);
    
    const maxRetries = 3;
    let retryCount = 0;
    
    const url = process.env.REACT_APP_API_ROUTE + "/daily-clearing-settlement";
    
    let data = {
      version: "04.08.0X",
      acquirer: {
        callbackURL: "https://daily-clearing&settlement.merchantpartners.com",
        timeStamp: new Date().toISOString(),
      },
      fiat: {
        currencyCode: currency,
      },
      report: {
        date: localDayStartToUtcDate(reportDate, "YYYYMMDD"),
      },
      reqhashMAC: "",
    };
    
    const dataString = JSON.stringify(data);
    const hash = SHA256(dataString + token).toString(enc.Base64);
    data.reqhashMAC = hash;
    
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
      acquirerName: acquirerData.acquirerName,
      acquirerID: acquirerData.acquirerId,
    };
    
    while (retryCount < maxRetries) {
      try {
        setRetryCount(retryCount);
        const response = await axios.post(url, data, { headers });
        
        if (response.data?.holdings?.holding?.length > 0) {
          const dataFormat = jsonToCsv(response.data);
          setCsvData(dataFormat);
          setData(response.data);
          setHide(false);
          setSnackOpen(false);
          setSeverity("success");
          setLoading(false);
          return; 
        } else {
          throw new Error("No transactions found for this date");
        }
        
      } catch (error) {
        retryCount++;
        console.error(`Attempt ${retryCount} failed:`, error);
        
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    setData('')
    setSeverity("error");
    setMessageOpen(
      "No transaction for this date"
    );
    setSnackOpen(true);
    setHide(true);
    setLoading(false);
  };

  return (
    <>
      <LoadingBackdrop loading={loading} retryCount={retryCount} />

      <form onSubmit={handleSubmit}>
        <Grid container spacing={isMobile ? 2 : 4}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth disabled> 
              <InputLabel id="currency-label">Currency Code</InputLabel>
                <Select
                  labelId="currency-label"
                  id="currency"
                  value={currency || ""}
                  label="Currency Code"
                  onChange={(event) => setCurrency(event.target.value)}
                  required
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="BRL">BRL</MenuItem> 
                  <MenuItem value="MXN">MXN</MenuItem>
                  <MenuItem value="COP">COP</MenuItem>
                  <MenuItem value="BOB">BOB</MenuItem>
                  <MenuItem value="VES">VES</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={isMobile ? 12 : 4}>
            <TextField
              fullWidth
              id="report-date"
              label="Report Date"
              type="date"
              value={reportDate}
              onChange={handleDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: minDate(),
                max: maxDate(),
              }}
              required
            />
          </Grid>
          <Grid
            item
            xs={isMobile ? 6 : undefined}
            textAlign={isMobile ? "center" : undefined}
          >
            <Button 
              variant="contained" 
              type="submit" 
              size="large"
              disabled={loading}
            >
               {loading ? 'GENERATING...' : 'GENERATE'}
            </Button>
          </Grid>
          <Grid
            item
            xs={isMobile ? 6 : undefined}
            textAlign={isMobile ? "center" : undefined}
          >
            <Button
              variant="contained"
              size="large"
              disabled={hide || loading}
              onClick={handleDownload}
            >
              DOWNLOAD
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default ReportForm;